import React, { useState, useEffect, useRef } from 'react';
import { fiscalLiberado } from './FiscalGuard';
import { Link, useLocation } from 'react-router-dom';
import axios from 'axios';
import '../styles/sidebar.css';
import logo from '../assets/logo-comece.svg';

// Lê id_bpo sincronamente: tenta JWT primeiro, depois localStorage como fallback
const _lerIdBpoDoToken = () => {
  try {
    const token = localStorage.getItem('access');
    if (!token) return null;
    const payload = JSON.parse(atob(token.split('.')[1]));
    if (payload.id_bpo !== undefined) return payload.id_bpo;
    // fallback: valor já resolvido em sessão anterior / por ClienteRoute
    const cached = localStorage.getItem('id_bpo');
    return cached !== null ? parseInt(cached, 10) : null;
  } catch (e) {
    return null;
  }
};

// Lê o user_id (PK do usuário no Django) do JWT — usado para liberar o menu "Testes".
const _lerUserIdDoToken = () => {
  try {
    const token = localStorage.getItem('access');
    if (!token) return null;
    const payload = JSON.parse(atob(token.split('.')[1]));
    return payload.user_id !== undefined ? payload.user_id : null;
  } catch (e) {
    return null;
  }
};

// Categorias do BPO RH — lista completa exibida de imediato no submenu;
// a API /api/bporh-categorias/ atualiza por cima quando responder (categorias
// novas cadastradas na página entram automaticamente).
const CATEGORIAS_BPORH_PADRAO = [
  'Assinador Digital',
  'Chat do cliente',
  'Ponto',
];

// Categorias VIRTUAIS: não existem em bporh_servico.categoria — a página
// /BpoRh/:categoria monta a lista a partir de outra categoria (Farmácia = os
// convênios de farmácia guardados em 'Benefícios'). Entram sempre no submenu.
// Farmácia saiu do menu em 23/09/2026 (pedido do Eleandro); a rota continua.
const CATEGORIAS_BPORH_VIRTUAIS = [];

// Categorias que existem no banco mas NÃO aparecem como submenu
// (os acessos continuam visíveis na página Clientes e Acessos)
// 'Home office' e 'Vale Transporte' já têm tela própria (Auxílio Home Office
// e Controle de VT, nos liberados) — a categoria não precisa aparecer no 'Em breve'.
// 'Benefícios', 'Agente Integrador' e 'Comunicação Contabilidade' saíram do menu
// em 21/09/2026 e 'Academia', 'Plano de Saúde e Odonto', 'Medicina do Trabalho' e
// 'Farmácia' em 23/09/2026 (pedido do Eleandro): VA e VT têm controle próprio e
// os acessos de todas continuam na página Clientes e Acessos.
const CATEGORIAS_BPORH_OCULTAS = ['Email', 'Gestão', 'Ferramentas', 'Controle de documentos',
  'Home office', 'Vale Transporte', 'VA', 'Benefícios', 'Agente Integrador',
  'Comunicação Contabilidade', 'Academia', 'Plano de Saúde e Odonto',
  'Medicina do Trabalho', 'Farmácia'];

// Atividades do BPO RH — cada uma vira um item de submenu (/Dp/:atividade)
const ATIVIDADES_BPORH = [
  { nome: 'Admissões', icone: 'fa-user-plus' },
  { nome: 'Rescisões', icone: 'fa-user-minus' },
  { nome: 'Férias', icone: 'fa-umbrella-beach' },
  { nome: 'Folha de Pagamento', icone: 'fa-file-invoice-dollar' },
  { nome: 'Prorrogação Colaborador', icone: 'fa-user-clock' },
  { nome: 'Controle de Estágio', icone: 'fa-graduation-cap' },
  { nome: 'Exame Mensal', icone: 'fa-notes-medical' },
  { nome: 'Sindicatos', icone: 'fa-handshake' },
];

// Menu do BPO RH em grupos (21/10/2026): a lista única tinha ~35 itens. Cada grupo
// abre/fecha sozinho; item liberado que não estiver em grupo nenhum cai em 'Outros'.
const GRUPOS_BPORH = [
  { id: 'inicio', nome: null, itens: ['Painel do Operador', 'Documentação'] },
  { id: 'gestao', nome: 'Gestão', icone: 'fa-chart-line', itens: ['Gestão do BPO RH', 'Atividades Mensais'] },
  { id: 'atendimento', nome: 'Atendimento', icone: 'fa-headset', itens: ['Conversas', 'Agente Nina', 'Agente de Currículos'] },
  { id: 'movimentacoes', nome: 'Admissão e desligamento', icone: 'fa-people-arrows',
    itens: ['Admissões', 'Rescisões', 'Prorrogação Colaborador', 'Controle de Estágio', 'Exame Mensal'] },
  { id: 'ponto', nome: 'Férias e ponto', icone: 'fa-clock', itens: ['Férias', 'Ponto', 'Resumo de Ponto', 'Controle de Folgas'] },
  { id: 'beneficios', nome: 'Benefícios', icone: 'fa-gift', itens: ['Controle de VA', 'Controle de VT', 'Auxílio Home Office', 'Plano de Saúde'] },
  { id: 'fechamento', nome: 'Folha e fechamento', icone: 'fa-file-invoice-dollar',
    itens: ['Folha de Pagamento', 'Fechamento do mês', 'Resumo centro de custo', 'Adiantamento salarial', 'Financeiro'] },
  { id: 'remuneracao', nome: 'Salários e custos', icone: 'fa-money-bill-trend-up',
    itens: ['Ajuste de salário', 'Dissídio', 'Custo por Colaborador'] },
  { id: 'utilitarios', nome: 'Utilitários', icone: 'fa-toolbox', itens: ['Importação Omie', 'Sindicatos'] },
  { id: 'cadastros', nome: 'Cadastros', icone: 'fa-database', itens: ['Clientes', 'Colaboradores', 'Operadoras'] },
];
const CHAVE_GRUPOS_BPORH = 'sidebar_grupos_bporh';

// Ícones do submenu dinâmico de categorias do BPO RH (fallback: fa-folder-open)
const ICONE_CATEGORIA_BPORH = {
  'Ponto': 'fa-clock',
  'Gestão': 'fa-people-group',
  'Medicina do Trabalho': 'fa-stethoscope',
  'Plano de Saúde e Odonto': 'fa-heart-pulse',
  'Vale Transporte': 'fa-bus',
  'Benefícios': 'fa-gift',
  'Farmácia': 'fa-prescription-bottle-medical',
  'Ferramentas': 'fa-toolbox',
  'Academia': 'fa-dumbbell',
  'Chat do cliente': 'fa-comments',
  'Assinador Digital': 'fa-file-signature',
  'Controle de documentos': 'fa-file-circle-check',
  'Agente Integrador': 'fa-handshake',
  'Home office': 'fa-house-laptop',
  'Home oficce': 'fa-house-laptop',
};

const Sidebar = () => {
  const location = useLocation();
  const [username, setUsername] = useState('usuário');
  const [carregandoNome, setCarregandoNome] = useState(true);

  // Lê id_bpo sincronamente para evitar qualquer flash de menu errado
  const _idBpoInicial = _lerIdBpoDoToken();
  const [idBpo, setIdBpo] = useState(_idBpoInicial);
  const [isCliente, setIsCliente] = useState(_idBpoInicial !== null && _idBpoInicial > 0);
  // Só renderiza o menu quando soubermos com certeza qual mostrar
  const [inicializado, setInicializado] = useState(_idBpoInicial !== null);

  // user_id do JWT — controla a visibilidade do menu "Testes" (só Eleandro, id 2)
  const [userId] = useState(_lerUserIdDoToken());

  // Permissão do Memor.IA — vem de auth_user.memora_ia ('sim' libera o menu)
  const [memoraIA, setMemoraIA] = useState(false);
  // Grupo do usuário (auth_user.grupo) — 'adm' libera Análise Gestor, Riscos e Intelligence
  const [grupoUsuario, setGrupoUsuario] = useState('');
  const fiscalNovoLiberado = fiscalLiberado(grupoUsuario, userId);

  const [menuAberto, setMenuAberto] = useState({
    fiscal: false,
    contabil: false,
    dp: false,
    departamentoPessoal: false,
    bpo: false,
    liga: false,
    tecnologia: false,
    marketing: false,
    administrativo: false,
    memoraia: false,
    testes: false,
    administracao: false,
  });

  const [sidebarAberta, setSidebarAberta] = useState(false);
  const [tooltipVisivel, setTooltipVisivel] = useState(null);
  const [tooltipPosition, setTooltipPosition] = useState({ top: 0, left: 0 });
  const timeoutRef = useRef(null);

  // grupos do BPO RH abertos/fechados — lembrados no navegador
  const [gruposBpoRh, setGruposBpoRh] = useState(() => {
    try { return JSON.parse(localStorage.getItem(CHAVE_GRUPOS_BPORH) || '{}') || {}; } catch (_) { return {}; }
  });
  const toggleGrupoBpoRh = (id, abertoAgora) => {
    setGruposBpoRh((prev) => {
      const novo = { ...prev, [id]: !abertoAgora };
      try { localStorage.setItem(CHAVE_GRUPOS_BPORH, JSON.stringify(novo)); } catch (_) { /* sem storage */ }
      return novo;
    });
  };

  const toggleMenu = (menu) => {
    setMenuAberto(prev => ({
      ...prev,
      [menu]: !prev[menu]
    }));
  };

  const handleMouseEnter = (itemId, event) => {
    const rect = event.currentTarget.getBoundingClientRect();
    setTooltipPosition({
      top: rect.top + (rect.height / 2),
      left: rect.right + 20
    });
    
    timeoutRef.current = setTimeout(() => {
      setTooltipVisivel(itemId);
    }, 5000);
  };

  const handleMouseLeave = () => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    setTooltipVisivel(null);
  };

  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  // Categorias do BPO RH — cada uma vira um item de submenu (/BpoRh/:categoria).
  // Começa com a lista padrão completa e é atualizada pela API quando disponível.
  const [categoriasBpoRh, setCategoriasBpoRh] = useState(CATEGORIAS_BPORH_PADRAO);

  useEffect(() => {
    if (!inicializado || isCliente) return;
    const accessToken = localStorage.getItem('access');
    if (!accessToken) return;

    axios
      .get(`${process.env.REACT_APP_API_BASE}/api/bporh-categorias/`, {
        headers: { Authorization: `Bearer ${accessToken}` }
      })
      .then((res) => {
        const daApi = (res.data.categorias || []).filter(c =>
          !CATEGORIAS_BPORH_OCULTAS.includes(c) && !/^https?:\/\//i.test(c));
        if (daApi.length > 0) {
          setCategoriasBpoRh([...daApi, ...CATEGORIAS_BPORH_VIRTUAIS.filter(v => !daApi.includes(v))]
            .sort((a, b) => a.localeCompare(b, 'pt-BR')));
        }
      })
      .catch(() => {});
  }, [inicializado, isCliente]);

  // LER id_bpo DO JWT
  const getIdBpoFromToken = (token) => {
    try {
      const payload = JSON.parse(atob(token.split('.')[1]));
      return payload.id_bpo !== undefined ? payload.id_bpo : null;
    } catch (e) {
      return null;
    }
  };

  const buscarDadosUsuario = async () => {
    try {
      const accessToken = localStorage.getItem('access');
      
      if (!accessToken) {
        setCarregandoNome(false);
        return;
      }

      // LER id_bpo DO JWT (SEGURO, ASSINADO PELO SERVIDOR)
      let id_bpo_jwt = getIdBpoFromToken(accessToken);

      // FALLBACK: Se não estiver no JWT, buscar da API
      if (id_bpo_jwt === null) {
        const response = await axios.get(
          `${process.env.REACT_APP_API_BASE}/api/verificar-grupo/`,
          {
            headers: { Authorization: `Bearer ${accessToken}` }
          }
        );

        if (response.data.success) {
          const nomeUsuario = response.data.name || 
                             response.data.first_name || 
                             response.data.usuario || 
                             'usuário';
          
          setUsername(nomeUsuario);
          setMemoraIA((response.data.memora_ia || '').toLowerCase().trim() === 'sim');
          setGrupoUsuario((response.data.grupo || '').toLowerCase().trim());

          id_bpo_jwt = response.data.id_bpo;
        } else {
          setUsername('usuário');
        }
      } else {
        // Se tem id_bpo no JWT, ainda buscar o nome da API
        try {
          const response = await axios.get(
            `${process.env.REACT_APP_API_BASE}/api/verificar-grupo/`,
            {
              headers: { Authorization: `Bearer ${accessToken}` }
            }
          );

          if (response.data.success) {
            const nomeUsuario = response.data.name ||
                               response.data.first_name ||
                               response.data.usuario ||
                               'usuário';
            setUsername(nomeUsuario);
            setMemoraIA((response.data.memora_ia || '').toLowerCase().trim() === 'sim');
            setGrupoUsuario((response.data.grupo || '').toLowerCase().trim());
          }
        } catch (error) {
          console.error('Erro ao buscar nome do usuário:', error);
        }
      }

      // LÓGICA:
      // id_bpo === 0 ou null → Operador interno (menu completo)
      // id_bpo > 0 → Cliente (menu específico)
      const isClienteUsuario = id_bpo_jwt > 0;

      // Atualizar estados
      setIdBpo(id_bpo_jwt);
      setIsCliente(isClienteUsuario);
      setInicializado(true);

      // Atualizar localStorage apenas para exibição (não para segurança)
      localStorage.setItem('id_bpo', id_bpo_jwt);
      localStorage.setItem('isCliente', isClienteUsuario ? 'true' : 'false');

    } catch (error) {
      console.error('Erro ao buscar dados do usuário:', error);
      setUsername('usuário');
      setInicializado(true);
    } finally {
      setCarregandoNome(false);
    }
  };

  useEffect(() => {
    buscarDadosUsuario();
  }, []);

  useEffect(() => {
    setSidebarAberta(false);
  }, [location.pathname]);

  // Dados do cliente BPO (sistema + módulos liberados) — só para logins de cliente.
  // Começa pelo cache da sessão (mesmo cliente) para o menu não "piscar" vazio no F5;
  // a API sempre é consultada e atualiza o cache. Sem cache e sem resposta ainda =
  // null = NENHUM módulo aparece (fail-closed) — antes o padrão era mostrar tudo.
  const _chaveCacheModulos = idBpo ? `fmp_cliente_bpo_${idBpo}` : null;
  const _lerCacheModulos = () => {
    if (!_chaveCacheModulos) return null;
    try {
      const raw = sessionStorage.getItem(_chaveCacheModulos);
      return raw ? JSON.parse(raw) : null;
    } catch (e) { return null; }
  };
  const [clienteBpo, setClienteBpo] = useState(_lerCacheModulos);
  useEffect(() => {
    if (!inicializado || !isCliente || !idBpo) return undefined;
    let ativo = true;
    const token = localStorage.getItem('access');
    axios.get(`${process.env.REACT_APP_API_BASE}/api/cliente-bpo/${idBpo}/`, {
      headers: { Authorization: `Bearer ${token}` },
    }).then((r) => {
      if (!ativo || !r.data?.success) return;
      setClienteBpo(r.data.cliente);
      try { sessionStorage.setItem(`fmp_cliente_bpo_${idBpo}`, JSON.stringify(r.data.cliente)); } catch (e) { /* sem cache */ }
    }).catch(() => {});
    return () => { ativo = false; };
  }, [inicializado, isCliente, idBpo]);

  // Enquanto os módulos do cliente não chegaram: nada liberado (fail-closed).
  // Depois: visível a menos que esteja explicitamente 'Não' (coluna ausente = liberado).
  const moduloAtivo = (flag) => clienteBpo !== null && ((clienteBpo?.[flag]) || 'Sim') !== 'Não';

  // ===================================
  // MENU PARA CLIENTES (BASEADO EM id_bpo)
  // ===================================
  const renderMenuCliente = () => {
    if (!idBpo || idBpo === 0) return null;

    const rotaDashboard = (clienteBpo?.sistema || '').toLowerCase() === 'conta_azul'
      ? `/dashboard-conta-azul/${idBpo}`
      : `/dashboard-cliente/${idBpo}`;

    const itemCliente = (rota, icone, cor, rotulo) => (
      <Link
        key={rota}
        to={rota}
        className={`nav-link nav-link-beat ${location.pathname === rota ? 'active' : ''}`}
      >
        <i className={`fas ${icone} me-2 fa-lg`} style={cor ? { color: cor } : undefined}></i>
        <span className="sidebar-info">{rotulo}</span>
      </Link>
    );

    return (
      <nav className="nav flex-column">
        {itemCliente(rotaDashboard, 'fa-home', null, 'Dashboard')}

        <div className="sidebar-divider my-2">
          <small
            className="text-muted px-3"
            style={{
              fontSize: '0.6rem',
              textTransform: 'uppercase',
              letterSpacing: '1px',
              color: '#ffffff'
            }}
          >
            Financeiro
          </small>
        </div>

        {moduloAtivo('mod_contas_pagar') && itemCliente(`/Apagar_cliente/${idBpo}`, 'fa-file-invoice-dollar', '#e74c3c', 'Contas a Pagar')}
        {moduloAtivo('mod_contas_receber') && itemCliente(`/Receber_cliente/${idBpo}`, 'fa-hand-holding-usd', '#27ae60', 'Contas a Receber')}
        {moduloAtivo('mod_posicao') && itemCliente(`/Posicao_cliente/${idBpo}`, 'fa-chart-line', '#3498db', 'Posição Financeira')}
        {moduloAtivo('mod_saving') && itemCliente(`/Saving_cliente/${idBpo}`, 'fa-piggy-bank', '#1fa971', 'Saving')}
        {moduloAtivo('mod_projecao_orcamentaria') && itemCliente(`/Planejamento_orcamentario/${idBpo}`, 'fa-chart-pie', '#2980b9', 'Projeção Orçamentária')}
        {moduloAtivo('mod_dre_vertical') && itemCliente(`/Dre_vertical/${idBpo}`, 'fa-chart-column', '#00b5cc', 'DRE Vertical')}
        {moduloAtivo('mod_dre_horizontal') && itemCliente(`/Dre_horizontal/${idBpo}`, 'fa-table-list', '#f1be48', 'DRE Horizontal')}
        {moduloAtivo('mod_projetos') && itemCliente(`/Projetos_cliente/${idBpo}`, 'fa-project-diagram', '#f39c12', 'Projetos')}

        <div className="sidebar-divider my-2">
          <small className="text-muted px-3" style={{ fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '1px', color: '#ffffff' }}>
            Contábil
          </small>
        </div>

        {moduloAtivo('mod_fechamento') && itemCliente(`/Fechamento_cliente/${idBpo}`, 'fa-calendar-check', '#9b59b6', 'Fechamento Mensal')}
        {moduloAtivo('mod_envio_notas') && itemCliente(`/EnvioNotas_cliente/${idBpo}`, 'fa-file-export', '#16a085', 'Envio de Notas')}
      </nav>
    );
  };
    
  // ===================================
  // MENU PARA FUNCIONÁRIOS (INTERNO)
  // ===================================
  const renderMenuInterno = () => (
    <nav className="nav flex-column">
      <Link to="/home" className={`nav-link nav-link-beat ${location.pathname === '/home' ? 'active' : ''}`}>
        <i className="fas fa-home me-2 fa-lg"></i>
        <span className="sidebar-info">Home</span>
      </Link>

      {/* MENU FISCAL */}
      <div className="menu-section">
        <div 
          className={`nav-link nav-link-beat menu-header ${menuAberto.fiscal ? 'menu-aberto' : ''}`}
          onClick={() => toggleMenu('fiscal')}
        >
          <i className="fa-solid fa-file-invoice me-2 fa-lg"></i>
          <span className="sidebar-info">Fiscal</span>
          <i className={`fas fa-chevron-down chevron-icon ${menuAberto.fiscal ? 'rotated' : ''}`}></i>
        </div>
        
        <div className={`submenu submenu-scroll ${menuAberto.fiscal ? 'submenu-aberto' : ''}`}>
          {/* Módulos novos do Fiscal (port do ZADRA): só grupo adm + Thiago — ver FiscalGuard.js */}
          {fiscalNovoLiberado && (<>
          <div 
            className="tooltip-wrapper"
            onMouseEnter={(e) => handleMouseEnter('fiscal-como-usar', e)}
            onMouseLeave={handleMouseLeave}
          >
            <Link
              to="/Fiscal/ComoUsar"
              className={`nav-link nav-link-beat submenu-item ${location.pathname === '/Fiscal/ComoUsar' ? 'active' : ''}`}
            >
              <i className="fa-solid fa-book-open me-2"></i>
              <span className="sidebar-info">Como usar este módulo</span>
            </Link>
          </div>

          <div 
            className="tooltip-wrapper"
            onMouseEnter={(e) => handleMouseEnter('fiscal-painel', e)}
            onMouseLeave={handleMouseLeave}
          >
            <Link
              to="/Fiscal/Painel"
              className={`nav-link nav-link-beat submenu-item ${location.pathname === '/Fiscal/Painel' ? 'active' : ''}`}
            >
              <i className="fa-solid fa-gauge-high me-2"></i>
              <span className="sidebar-info">Painel do Operador</span>
            </Link>
          </div>

          <div 
            className="tooltip-wrapper"
            onMouseEnter={(e) => handleMouseEnter('fiscal-base', e)}
            onMouseLeave={handleMouseLeave}
          >
            <Link
              to="/Fiscal/Base"
              className={`nav-link nav-link-beat submenu-item ${location.pathname === '/Fiscal/Base' ? 'active' : ''}`}
            >
              <i className="fa-solid fa-building-columns me-2"></i>
              <span className="sidebar-info">Base Fiscal</span>
            </Link>
          </div>

          <div 
            className="tooltip-wrapper"
            onMouseEnter={(e) => handleMouseEnter('fiscal-monitor', e)}
            onMouseLeave={handleMouseLeave}
          >
            <Link
              to="/Fiscal/Monitor"
              className={`nav-link nav-link-beat submenu-item ${location.pathname === '/Fiscal/Monitor' ? 'active' : ''}`}
            >
              <i className="fa-solid fa-satellite-dish me-2"></i>
              <span className="sidebar-info">Monitor de NFS-e</span>
            </Link>
          </div>

          <div 
            className="tooltip-wrapper"
            onMouseEnter={(e) => handleMouseEnter('fiscal-fechamento', e)}
            onMouseLeave={handleMouseLeave}
          >
            <Link
              to="/Fiscal/Fechamento"
              className={`nav-link nav-link-beat submenu-item ${location.pathname === '/Fiscal/Fechamento' ? 'active' : ''}`}
            >
              <i className="fa-solid fa-file-excel me-2"></i>
              <span className="sidebar-info">Fechamento Mensal</span>
            </Link>
          </div>

          <div 
            className="tooltip-wrapper"
            onMouseEnter={(e) => handleMouseEnter('fiscal-consulta-chave', e)}
            onMouseLeave={handleMouseLeave}
          >
            <Link
              to="/Fiscal/ConsultaChave"
              className={`nav-link nav-link-beat submenu-item ${location.pathname === '/Fiscal/ConsultaChave' ? 'active' : ''}`}
            >
              <i className="fa-solid fa-key me-2"></i>
              <span className="sidebar-info">Consulta NFS-e por chave</span>
            </Link>
          </div>

          <div 
            className="tooltip-wrapper"
            onMouseEnter={(e) => handleMouseEnter('fiscal-faturamento', e)}
            onMouseLeave={handleMouseLeave}
          >
            <Link
              to="/Fiscal/Faturamento"
              className={`nav-link nav-link-beat submenu-item ${location.pathname === '/Fiscal/Faturamento' ? 'active' : ''}`}
            >
              <i className="fa-solid fa-file-invoice-dollar me-2"></i>
              <span className="sidebar-info">Declaração de Faturamento</span>
            </Link>
          </div>

          <div 
            className="tooltip-wrapper"
            onMouseEnter={(e) => handleMouseEnter('fiscal-entregas', e)}
            onMouseLeave={handleMouseLeave}
          >
            <Link
              to="/Fiscal/Entregas"
              className={`nav-link nav-link-beat submenu-item ${location.pathname === '/Fiscal/Entregas' ? 'active' : ''}`}
            >
              <i className="fa-solid fa-calendar-check me-2"></i>
              <span className="sidebar-info">Entregas no Calendário</span>
            </Link>
          </div>

          <div 
            className="tooltip-wrapper"
            onMouseEnter={(e) => handleMouseEnter('fiscal-tarefas', e)}
            onMouseLeave={handleMouseLeave}
          >
            <Link
              to="/Fiscal/Tarefas"
              className={`nav-link nav-link-beat submenu-item ${location.pathname === '/Fiscal/Tarefas' ? 'active' : ''}`}
            >
              <i className="fa-solid fa-list-check me-2"></i>
              <span className="sidebar-info">Tarefas Extras</span>
            </Link>
          </div>

          <div 
            className="tooltip-wrapper"
            onMouseEnter={(e) => handleMouseEnter('fiscal-tarefas-acessorias', e)}
            onMouseLeave={handleMouseLeave}
          >
            <Link
              to="/Fiscal/TarefasAcessorias"
              className={`nav-link nav-link-beat submenu-item ${location.pathname === '/Fiscal/TarefasAcessorias' ? 'active' : ''}`}
            >
              <i className="fa-solid fa-clipboard-check me-2"></i>
              <span className="sidebar-info">Tarefas Acessórias</span>
            </Link>
          </div>

          <div 
            className="tooltip-wrapper"
            onMouseEnter={(e) => handleMouseEnter('fiscal-usuarios', e)}
            onMouseLeave={handleMouseLeave}
          >
            <Link
              to="/Fiscal/Usuarios"
              className={`nav-link nav-link-beat submenu-item ${location.pathname === '/Fiscal/Usuarios' ? 'active' : ''}`}
            >
              <i className="fa-solid fa-users-gear me-2"></i>
              <span className="sidebar-info">Usuários</span>
            </Link>
          </div>

          <div 
            className="tooltip-wrapper"
            onMouseEnter={(e) => handleMouseEnter('fiscal-certificados', e)}
            onMouseLeave={handleMouseLeave}
          >
            <Link
              to="/Fiscal/Certificados"
              className={`nav-link nav-link-beat submenu-item ${location.pathname === '/Fiscal/Certificados' ? 'active' : ''}`}
            >
              <i className="fa-solid fa-certificate me-2"></i>
              <span className="sidebar-info">Certificados A1</span>
            </Link>
          </div>

          <div 
            className="tooltip-wrapper"
            onMouseEnter={(e) => handleMouseEnter('fiscal-config', e)}
            onMouseLeave={handleMouseLeave}
          >
            <Link
              to="/Fiscal/Configuracoes"
              className={`nav-link nav-link-beat submenu-item ${location.pathname === '/Fiscal/Configuracoes' ? 'active' : ''}`}
            >
              <i className="fa-solid fa-sliders me-2"></i>
              <span className="sidebar-info">Configurações Fiscais</span>
            </Link>
          </div>

          </>)}
          <div 
            className="tooltip-wrapper"
            onMouseEnter={(e) => handleMouseEnter('relatorio-fiscal', e)}
            onMouseLeave={handleMouseLeave}
          >
            <Link
              to="/relatorio-fiscal"
              className={`nav-link nav-link-beat submenu-item ${location.pathname === '/relatorio-fiscal' ? 'active' : ''}`}
            >
              <i className="fa-solid fa-folder-open me-2"></i>
              <span className="sidebar-info">Relatórios Fiscais</span>
            </Link>
          </div>
          
          <div 
            className="tooltip-wrapper"
            onMouseEnter={(e) => handleMouseEnter('dividas', e)}
            onMouseLeave={handleMouseLeave}
          >
            <Link
              to="/Dividas"
              className={`nav-link nav-link-beat submenu-item ${location.pathname === '/Dividas' ? 'active' : ''}`}
            >
              <i className="fa-solid fa-dollar-sign me-2"></i>
              <span className="sidebar-info">Pendência Fiscal</span>
            </Link>
          </div>
          
          <div 
            className="tooltip-wrapper"
            onMouseEnter={(e) => handleMouseEnter('dctfweb', e)}
            onMouseLeave={handleMouseLeave}
          >
            <Link
              to="/Dctfweb"
              className={`nav-link nav-link-beat submenu-item ${location.pathname === '/Dctfweb' ? 'active' : ''}`}
            >
              <i className="fa-solid fa-file-invoice me-2"></i>
              <span className="sidebar-info">DCTFWEB</span>
            </Link>
          </div>
        </div>
      </div>

      {/* MENU CONTÁBIL */}
      <div className="menu-section">
        <div 
          className={`nav-link nav-link-beat menu-header ${menuAberto.contabil ? 'menu-aberto' : ''}`}
          onClick={() => toggleMenu('contabil')}
        >
          <i className="fa-solid fa-scale-unbalanced-flip me-2 fa-lg"></i>
          <span className="sidebar-info">Contábil</span>
          <i className={`fas fa-chevron-down chevron-icon ${menuAberto.contabil ? 'rotated' : ''}`}></i>
        </div>
        
        <div className={`submenu ${menuAberto.contabil ? 'submenu-aberto submenu-scroll' : ''}`}>
          <div
            className="tooltip-wrapper"
            onMouseEnter={(e) => handleMouseEnter('contabilidade', e)}
            onMouseLeave={handleMouseLeave}
          >
            <Link
              to="/Contabilidade"
              className={`nav-link nav-link-beat submenu-item ${location.pathname === '/Contabilidade' ? 'active' : ''}`}
            >
              <i className="fa-solid fa-calculator me-2"></i>
              <span className="sidebar-info">Contábil</span>
            </Link>
          </div>
          {/* <div
            className="tooltip-wrapper"
            onMouseEnter={(e) => handleMouseEnter('portal-cliente', e)}
            onMouseLeave={handleMouseLeave}
          >
            <Link
              to="/portal-cliente"
              className={`nav-link nav-link-beat submenu-item ${location.pathname.startsWith('/portal-cliente') ? 'active' : ''}`}
            >
              <i className="fa-solid fa-address-card me-2"></i>
              <span className="sidebar-info">Portal do Cliente</span>
            </Link>
          </div> */}

          <div
            className="tooltip-wrapper"
            onMouseEnter={(e) => handleMouseEnter('revisao-relatorios', e)}
            onMouseLeave={handleMouseLeave}
          >
            <Link
              to="/RevisaoRelatorios"
              className={`nav-link nav-link-beat submenu-item ${location.pathname === '/RevisaoRelatorios' ? 'active' : ''}`}
            >
              <i className="fa-solid fa-user-shield me-2"></i>
              <span className="sidebar-info">Revisão de Envios</span>
            </Link>
          </div>

          <div
            className="tooltip-wrapper"
            onMouseEnter={(e) => handleMouseEnter('sped-contabil', e)}
            onMouseLeave={handleMouseLeave}
          >
            <Link
              to="/SpedContabil"
              className={`nav-link nav-link-beat submenu-item ${location.pathname === '/SpedContabil' ? 'active' : ''}`}
            >
              <i className="fa-solid fa-file-invoice me-2"></i>
              <span className="sidebar-info">Sped Contábil</span>
            </Link>
          </div>
          
          <div 
            className="tooltip-wrapper"
            onMouseEnter={(e) => handleMouseEnter('Contabil-reunioes', e)}
            onMouseLeave={handleMouseLeave}
          >
            <Link
              to="/Contabil-reunioes"
              className={`nav-link nav-link-beat submenu-item ${location.pathname === '/ContabilReunioes' ? 'active' : ''}`}
            >
              <i className="fa-solid fa-file-invoice me-2"></i>
              <span className="sidebar-info">Reuniões Contábeis</span>
            </Link>
          </div>

          {/* NOVO: LALUR */}
          <div 
            className="tooltip-wrapper"
            onMouseEnter={(e) => handleMouseEnter('lalur', e)}
            onMouseLeave={handleMouseLeave}
          >
            <Link
              to="#"
              onClick={() => window.open('https://comece.app/lalur', '_blank')}
              className="nav-link nav-link-beat submenu-item"
            >
              <i className="fa-solid fa-book me-2" style={{ color: '#8e44ad' }}></i>
              <span className="sidebar-info">LALUR</span>
            </Link>
          </div>

          {/* Comece Vision (portal do cliente com indicadores e documentos) */}
          <div 
            className="tooltip-wrapper"
            onMouseEnter={(e) => handleMouseEnter('vision', e)}
            onMouseLeave={handleMouseLeave}
          >
            <Link
              to="#"
              onClick={() => window.open('https://vision.comece.app', '_blank')}
              className="nav-link nav-link-beat submenu-item"
            >
              <i className="fa-solid fa-rocket me-2" style={{ color: '#04c2d7' }}></i>
              <span className="sidebar-info">Vision</span>
            </Link>
          </div>
          
          <div 
            className="tooltip-wrapper"
            onMouseEnter={(e) => handleMouseEnter('carteira-contabil', e)}
            onMouseLeave={handleMouseLeave}
          >
            <Link
              to="/CarteiraContabil"
              className={`nav-link nav-link-beat submenu-item ${location.pathname === '/CarteiraContabil' ? 'active' : ''}`}
            >
              <i className="fa-solid fa-address-book me-2"></i>
              <span className="sidebar-info">Carteira Contábil</span>
              <i className="fas fa-star star-gestor"></i>
            </Link>
          </div>
          
          <div
            className="tooltip-wrapper"
            onMouseEnter={(e) => handleMouseEnter('gestao-contabil', e)}
            onMouseLeave={handleMouseLeave}
          >
            <Link
              to="/Gestao_contabil"
              className={`nav-link nav-link-beat submenu-item ${location.pathname === '/Gestao_contabil' ? 'active' : ''}`}
            >
              <i className="fa-solid fa-trophy me-2"></i>
              <span className="sidebar-info">Gestão Contábil</span>
              <i className="fas fa-star star-gestor"></i>
            </Link>
          </div>

          <div
            className="tooltip-wrapper"
            onMouseEnter={(e) => handleMouseEnter('devedores-contabil', e)}
            onMouseLeave={handleMouseLeave}
          >
            <Link
              to="/Devedores"
              className={`nav-link nav-link-beat submenu-item ${location.pathname === '/Devedores' ? 'active' : ''}`}
            >
              <i className="fa-solid fa-triangle-exclamation me-2" style={{ color: '#dc3545' }}></i>
              <span className="sidebar-info">Devedores</span>
            </Link>
          </div>

          <div
            className="tooltip-wrapper"
            onMouseEnter={(e) => handleMouseEnter('impostos-recup', e)}
            onMouseLeave={handleMouseLeave}
          >
            <Link
              to="/Impostos-recup"
              className={`nav-link nav-link-beat submenu-item ${location.pathname === '/Impostos-recup' ? 'active' : ''}`}
            >
              <i className="fa-solid fa-file-invoice-dollar me-2" style={{ color: '#198754' }}></i>
              <span className="sidebar-info">Impostos a Recuperar</span>
            </Link>
          </div>

          <div
            className="tooltip-wrapper"
            onMouseEnter={(e) => handleMouseEnter('auditoria-balancete', e)}
            onMouseLeave={handleMouseLeave}
          >
            <Link
              to="/auditoria-balancete"
              className={`nav-link nav-link-beat submenu-item ${location.pathname === '/auditoria-balancete' ? 'active' : ''}`}
            >
              <i className="fa-solid fa-magnifying-glass-dollar me-2" style={{ color: '#6f42c1' }}></i>
              <span className="sidebar-info">Auditoria de Balancete</span>
            </Link>
          </div>

          <div
            className="tooltip-wrapper"
            onMouseEnter={(e) => handleMouseEnter('historico-auditoria', e)}
            onMouseLeave={handleMouseLeave}
          >
            <Link
              to="/historico-auditoria"
              className={`nav-link nav-link-beat submenu-item ${location.pathname === '/historico-auditoria' ? 'active' : ''}`}
            >
              <i className="fa-solid fa-clock-rotate-left me-2" style={{ color: '#6f42c1' }}></i>
              <span className="sidebar-info">Histórico Auditoria</span>
            </Link>
          </div>

          <div
            className="tooltip-wrapper"
            onMouseEnter={(e) => handleMouseEnter('historico-envio-lucros-socios', e)}
            onMouseLeave={handleMouseLeave}
          >
            <Link
              to="/HistoricoEnvioLucrosSocios"
              className={`nav-link nav-link-beat submenu-item ${location.pathname === '/HistoricoEnvioLucrosSocios' ? 'active' : ''}`}
            >
              <i className="fa-solid fa-envelope-open-text me-2" style={{ color: '#0d6efd' }}></i>
              <span className="sidebar-info">Envio Lucros Sócios</span>
            </Link>
          </div>
        </div>
      </div>

      {/* MENU BPO RH — visível para os grupos 'rh' e 'adm' (auth_user.grupo) */}
      {['rh', 'adm'].includes(grupoUsuario) && (
      <div className="menu-section">
        <div
          className={`nav-link nav-link-beat menu-header ${menuAberto.dp ? 'menu-aberto' : ''}`}
          onClick={() => toggleMenu('dp')}
        >
          <i className="fa-solid fa-users me-2 fa-lg"></i>
          <span className="sidebar-info">BPO RH</span>
          <i className={`fas fa-chevron-down chevron-icon ${menuAberto.dp ? 'rotated' : ''}`}></i>
        </div>
        
        <div className={`submenu ${menuAberto.dp ? 'submenu-aberto submenu-scroll' : ''}`}>
          {(() => {
            // Itens liberados aparecem em cima (clicáveis); os demais ficam
            // embaixo com cadeado (em desenvolvimento). LIBERADOS_BPORH define
            // o que está pronto para uso.
            const LIBERADOS_BPORH = ['Documentação', 'Painel do Operador', 'Agente Nina', 'Agente de Currículos', 'Gestão do BPO RH', 'Fechamento do mês', 'Financeiro', 'Dissídio', 'Ajuste de salário', 'Importação Omie', 'Adiantamento salarial', 'Resumo centro de custo', 'Controle de Folgas', 'Plano de Saúde', 'Custo por Colaborador', 'Operadoras', 'Clientes', 'Colaboradores', 'Atividades Mensais', 'Admissões',
              'Rescisões', 'Férias', 'Ponto', 'Resumo de Ponto', 'Controle de VA',
              'Controle de VT', 'Auxílio Home Office', 'Conversas',
              'Prorrogação Colaborador', 'Controle de Estágio', 'Exame Mensal', 'Sindicatos',
              'Folha de Pagamento'];
            const itensBpoRh = [
              { nome: 'Documentação', icone: 'fa-book-open', to: '/BpoRh/Documentacao', tt: 'bporh-documentacao' },
              { nome: 'Painel do Operador', icone: 'fa-gauge-high', to: '/BpoRh/PainelOperador', tt: 'bporh-painel-operador' },
              { nome: 'Agente Nina', icone: 'fa-headset', to: '/BpoRh/AgenteRh', tt: 'bporh-agente' },
              // só adm (o backend também recusa os demais)
              ...(grupoUsuario === 'adm'
                ? [{ nome: 'Agente de Currículos', icone: 'fa-file-lines', to: '/BpoRh/Curriculos', tt: 'bporh-curriculos' }]
                : []),
              { nome: 'Gestão do BPO RH', icone: 'fa-chart-line', to: '/BpoRh/Gestao', tt: 'bporh-gestao' },
              { nome: 'Fechamento do mês', icone: 'fa-envelope-open-text', to: '/BpoRh/FechamentoCliente', tt: 'bporh-fechamento-cliente' },
              { nome: 'Financeiro', icone: 'fa-money-check-dollar', to: '/BpoRh/Financeiro', tt: 'bporh-financeiro' },
              { nome: 'Dissídio', icone: 'fa-percent', to: '/BpoRh/Dissidio', tt: 'bporh-dissidio' },
              { nome: 'Ajuste de salário', icone: 'fa-money-bill-trend-up', to: '/BpoRh/AjusteSalario', tt: 'bporh-ajuste-salario' },
              { nome: 'Importação Omie', icone: 'fa-file-export', to: '/BpoRh/ImportacaoOmie', tt: 'bporh-importacao-omie' },
              { nome: 'Adiantamento salarial', icone: 'fa-hand-holding-dollar', to: '/BpoRh/Adiantamento', tt: 'bporh-adiantamento' },
              { nome: 'Resumo centro de custo', icone: 'fa-sitemap', to: '/BpoRh/ResumoCentroCusto', tt: 'bporh-resumo-cc' },
              // abono assiduidade da CCT — por enquanto só a Setho
              { nome: 'Controle de Folgas', icone: 'fa-umbrella-beach', to: '/BpoRh/ControleFolgas', tt: 'bporh-controle-folgas' },
              { nome: 'Plano de Saúde', icone: 'fa-heart-pulse', to: '/BpoRh/PlanoSaude', tt: 'bporh-plano-saude' },
              { nome: 'Custo por Colaborador', icone: 'fa-calculator', to: '/BpoRh/CustoColaborador', tt: 'bporh-custo-colaborador' },
              { nome: 'Operadoras', icone: 'fa-id-badge', to: '/BpoRh/Operadores', tt: 'bporh-operadores' },
              { nome: 'Clientes', icone: 'fa-building', to: '/BpoRhClientesCadastro', tt: 'bporh-cadastro-clientes' },
              { nome: 'Colaboradores', icone: 'fa-address-book', to: '/BpoRhColaboradores', tt: 'bporh-colaboradores' },
              { nome: 'Atividades Mensais', icone: 'fa-calendar-check', to: '/BpoTarefas/RH', tt: 'bporh-tarefas-mensais' },
              { nome: 'Resumo de Ponto', icone: 'fa-file-circle-check', to: '/BpoRh/FechamentoPonto', tt: 'bporh-fechamento' },
              { nome: 'Controle de VA', icone: 'fa-utensils', to: '/BpoRh/ControleBeneficios', tt: 'bporh-beneficios' },
              { nome: 'Controle de VT', icone: 'fa-bus', to: '/BpoRh/ValeTransporte', tt: 'bporh-vale-transporte' },
              { nome: 'Auxílio Home Office', icone: 'fa-house-laptop', to: '/BpoRh/HomeOffice', tt: 'bporh-home-office' },
              { nome: 'Conversas', icone: 'fa-comments', to: '/BpoRh/Conversas', tt: 'bporh-conversas' },
              ...ATIVIDADES_BPORH.map(a => ({
                nome: a.nome, icone: a.icone,
                to: `/Dp/${encodeURIComponent(a.nome)}`, tt: `bporh-atividade-${a.nome}`,
              })),
              ...categoriasBpoRh.map(cat => ({
                nome: cat, icone: ICONE_CATEGORIA_BPORH[cat] || 'fa-folder-open',
                to: `/BpoRh/${encodeURIComponent(cat)}`, tt: `bporh-cat-${cat}`,
              })),
            ];
            const liberados = LIBERADOS_BPORH
              .map(n => itensBpoRh.find(i => i.nome === n)).filter(Boolean);
            const bloqueados = itensBpoRh.filter(i => !LIBERADOS_BPORH.includes(i.nome));
            return (
              <>
                {(() => {
                  const ativo = (item) => location.pathname === item.to
                    || decodeURIComponent(location.pathname) === decodeURIComponent(item.to);
                  const linkItem = (item, sub) => (
                    <div key={item.nome} className="tooltip-wrapper"
                      onMouseEnter={(e) => handleMouseEnter(item.tt, e)}
                      onMouseLeave={handleMouseLeave}>
                      <Link to={item.to}
                        className={`nav-link nav-link-beat submenu-item ${sub ? 'submenu-subitem' : ''} ${ativo(item) ? 'active' : ''}`}>
                        <i className={`fa-solid ${item.icone} me-2`}></i>
                        <span className="sidebar-info">{item.nome}</span>
                      </Link>
                    </div>
                  );
                  const usados = new Set(GRUPOS_BPORH.flatMap((g) => g.itens));
                  const outros = liberados.filter((i) => !usados.has(i.nome));
                  const grupos = [...GRUPOS_BPORH, ...(outros.length ? [{ id: 'outros', nome: 'Outros', icone: 'fa-ellipsis', itens: outros.map((i) => i.nome) }] : [])];
                  return grupos.map((g) => {
                    const itens = g.itens.map((n) => liberados.find((i) => i.nome === n)).filter(Boolean);
                    if (!itens.length) return null;
                    if (!g.nome) return itens.map((i) => linkItem(i, false));
                    // abre sozinho quando a tela atual é de dentro do grupo
                    const temAtivo = itens.some(ativo);
                    const aberto = gruposBpoRh[g.id] !== undefined ? (gruposBpoRh[g.id] || temAtivo) : temAtivo;
                    return (
                      <div key={g.id} className="submenu-grupo-bloco">
                        <div className={`nav-link nav-link-beat submenu-item submenu-grupo submenu-grupo-${g.id} ${temAtivo ? 'submenu-grupo-ativo' : ''}`}
                          role="button" tabIndex={0} aria-expanded={aberto}
                          onClick={() => toggleGrupoBpoRh(g.id, aberto)}
                          onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); toggleGrupoBpoRh(g.id, aberto); } }}>
                          <i className={`fa-solid ${g.icone} me-2`}></i>
                          <span className="sidebar-info">{g.nome}</span>
                          <i className={`fas fa-chevron-down submenu-grupo-seta ${aberto ? 'aberta' : ''}`}></i>
                        </div>
                        {aberto && itens.map((i) => linkItem(i, true))}
                      </div>
                    );
                  });
                })()}

                {bloqueados.length > 0 && (
                  <div className="submenu-item text-muted"
                    style={{ fontSize: '.72rem', letterSpacing: '.5px', opacity: .6,
                      padding: '8px 16px 4px', textTransform: 'uppercase', pointerEvents: 'none' }}>
                    Em breve
                  </div>
                )}
                {bloqueados.map((item) => (
                  <div key={item.nome} className="tooltip-wrapper"
                    onMouseEnter={(e) => handleMouseEnter(item.tt, e)}
                    onMouseLeave={handleMouseLeave}>
                    <Link to={item.to}
                      className={`nav-link nav-link-beat submenu-item ${location.pathname === item.to ? 'active' : ''}`}
                      title="Em desenvolvimento — pode acessar, mas ainda não está pronto"
                      style={{ opacity: .5 }}>
                      <i className={`fa-solid ${item.icone} me-2`}></i>
                      <span className="sidebar-info">{item.nome}</span>
                      <i className="fa-solid fa-lock ms-auto" style={{ fontSize: '.72rem' }}></i>
                    </Link>
                  </div>
                ))}
              </>
            );
          })()}
        </div>
      </div>
      )}

      {/* MENU DEPARTAMENTO PESSOAL */}
      <div className="menu-section">
        <div
          className={`nav-link nav-link-beat menu-header ${menuAberto.departamentoPessoal ? 'menu-aberto' : ''}`}
          onClick={() => toggleMenu('departamentoPessoal')}
        >
          <i className="fa-solid fa-id-badge me-2 fa-lg"></i>
          <span className="sidebar-info">Departamento Pessoal</span>
          <i className={`fas fa-chevron-down chevron-icon ${menuAberto.departamentoPessoal ? 'rotated' : ''}`}></i>
        </div>

        <div className={`submenu ${menuAberto.departamentoPessoal ? 'submenu-aberto' : ''}`}>
          <div
            className="tooltip-wrapper"
            onMouseEnter={(e) => handleMouseEnter('envio-email-dp', e)}
            onMouseLeave={handleMouseLeave}
          >
            <Link
              to="/Enviaremaildp"
              className={`nav-link nav-link-beat submenu-item ${location.pathname === '/Enviaremaildp' ? 'active' : ''}`}
            >
              <i className="fa-solid fa-envelope-open-text me-2"></i>
              <span className="sidebar-info">Envio Email DP</span>
            </Link>
          </div>

          <div
            className="tooltip-wrapper"
            onMouseEnter={(e) => handleMouseEnter('guia-fgts', e)}
            onMouseLeave={handleMouseLeave}
          >
            <Link
              to="/Guia-fgts"
              className={`nav-link nav-link-beat submenu-item ${location.pathname === '/Guia-fgts' ? 'active' : ''}`}
            >
              <i className="fa-solid fa-money-bill-transfer me-2"></i>
              <span className="sidebar-info">Guia FGTS</span>
            </Link>
          </div>
        </div>
      </div>

      {/* MENU BPO FINANCEIRO */}
      <div className="menu-section">
        <div 
          className={`nav-link nav-link-beat menu-header ${menuAberto.bpo ? 'menu-aberto' : ''}`}
          onClick={() => toggleMenu('bpo')}
        >
          <i className="fa-solid fa-building-columns me-2 fa-lg"></i>
          <span className="sidebar-info">BPO Financeiro</span>
          <i className={`fas fa-chevron-down chevron-icon ${menuAberto.bpo ? 'rotated' : ''}`}></i>
        </div>
        
        <div className={`submenu ${menuAberto.bpo ? 'submenu-aberto' : ''}`}>
          <div
            className="tooltip-wrapper"
            onMouseEnter={(e) => handleMouseEnter('fmp', e)}
            onMouseLeave={handleMouseLeave}
          >
            <Link
              to="/Bpo"
              className={`nav-link nav-link-beat submenu-item ${location.pathname === '/Bpo' ? 'active' : ''}`}
            >
              <i className="fa-solid fa-chart-line me-2"></i>
              <span className="sidebar-info">FMP</span>
            </Link>
          </div>

          <div
            className="tooltip-wrapper"
            onMouseEnter={(e) => handleMouseEnter('gestao-bpo', e)}
            onMouseLeave={handleMouseLeave}
          >
            <Link
              to="/GestaoBpo"
              className={`nav-link nav-link-beat submenu-item ${location.pathname === '/GestaoBpo' ? 'active' : ''}`}
            >
              <i className="fa-solid fa-table-list me-2"></i>
              <span className="sidebar-info">Gestão BPO</span>
            </Link>
          </div>

          <div
            className="tooltip-wrapper"
            onMouseEnter={(e) => handleMouseEnter('bpofin-tarefas-mensais', e)}
            onMouseLeave={handleMouseLeave}
          >
            <Link
              to="/BpoTarefas/FIN"
              className={`nav-link nav-link-beat submenu-item ${location.pathname === '/BpoTarefas/FIN' ? 'active' : ''}`}
            >
              <i className="fa-solid fa-calendar-check me-2"></i>
              <span className="sidebar-info">Atividades Mensais</span>
            </Link>
          </div>

          <div
            className="tooltip-wrapper"
            onMouseEnter={(e) => handleMouseEnter('bpofin-conciliacao-omie', e)}
            onMouseLeave={handleMouseLeave}
          >
            <Link
              to="/BpoFin/ConciliacaoOmie"
              className={`nav-link nav-link-beat submenu-item ${location.pathname === '/BpoFin/ConciliacaoOmie' ? 'active' : ''}`}
            >
              <i className="fa-solid fa-scale-balanced me-2"></i>
              <span className="sidebar-info">Conciliação Omie</span>
            </Link>
          </div>

          <div
            className="tooltip-wrapper"
            onMouseEnter={(e) => handleMouseEnter('bpofin-conciliacao-vendizap', e)}
            onMouseLeave={handleMouseLeave}
          >
            <Link
              to="/BpoFin/ConciliacaoVendizap"
              className={`nav-link nav-link-beat submenu-item ${location.pathname === '/BpoFin/ConciliacaoVendizap' ? 'active' : ''}`}
            >
              <i className="fa-solid fa-money-bill-transfer me-2"></i>
              <span className="sidebar-info">Conciliação Stripe Vend.</span>
            </Link>
          </div>

          <div
            className="tooltip-wrapper"
            onMouseEnter={(e) => handleMouseEnter('bpofin-conciliacao-asaas-vendizap', e)}
            onMouseLeave={handleMouseLeave}
          >
            <Link
              to="/BpoFin/ConciliacaoAsaasVendizap"
              className={`nav-link nav-link-beat submenu-item ${location.pathname === '/BpoFin/ConciliacaoAsaasVendizap' ? 'active' : ''}`}
            >
              <i className="fa-solid fa-qrcode me-2"></i>
              <span className="sidebar-info">Conciliação Asaas Vend.</span>
            </Link>
          </div>

          <div
            className="tooltip-wrapper"
            onMouseEnter={(e) => handleMouseEnter('bpofin-cartao-pixcards', e)}
            onMouseLeave={handleMouseLeave}
          >
            <Link
              to="/BpoFin/CartaoPixCards"
              className={`nav-link nav-link-beat submenu-item ${location.pathname === '/BpoFin/CartaoPixCards' ? 'active' : ''}`}
            >
              <i className="fa-solid fa-credit-card me-2"></i>
              <span className="sidebar-info">Cartão Pix Cards</span>
            </Link>
          </div>

        </div>
      </div>

      {/* NOVO MENU: ADMINISTRATIVO */}
      <div className="menu-section">
        <div 
          className={`nav-link nav-link-beat menu-header ${menuAberto.administrativo ? 'menu-aberto' : ''}`}
          onClick={() => toggleMenu('administrativo')}
        >
          <i className="fa-solid fa-building me-2 fa-lg" style={{ color: '#95a5a6' }}></i>
          <span className="sidebar-info">Administrativo</span>
          <i className={`fas fa-chevron-down chevron-icon ${menuAberto.administrativo ? 'rotated' : ''}`}></i>
        </div>

        <div className={`submenu ${menuAberto.administrativo ? 'submenu-aberto' : ''}`}>
          {/* ASPA */}
          <div
            className="tooltip-wrapper"
            onMouseEnter={(e) => handleMouseEnter('aspa', e)}
            onMouseLeave={handleMouseLeave}
          >
            <Link
              to="#"
              onClick={() => window.open('https://comece.app/aspa/dashboard', '_blank')}
              className="nav-link nav-link-beat submenu-item"
            >
              <i className="fa-solid fa-pen-to-square me-2" style={{ color: '#3498db' }}></i>
              <span className="sidebar-info">ASPA</span>
            </Link>
          </div>

          {/* INVENTÁRIO */}
          <div
            className="tooltip-wrapper"
            onMouseEnter={(e) => handleMouseEnter('inventario', e)}
            onMouseLeave={handleMouseLeave}
          >
            <Link
              to="#"
              onClick={() => window.open('https://comece.app/equipamentos/colaboradores', '_blank')}
              className="nav-link nav-link-beat submenu-item"
            >
              <i className="fa-solid fa-laptop me-2" style={{ color: '#7f8c8d' }}></i>
              <span className="sidebar-info">Inventário</span>
            </Link>
          </div>

          {/* PMO */}
          <div
            className="tooltip-wrapper"
            onMouseEnter={(e) => handleMouseEnter('pmo', e)}
            onMouseLeave={handleMouseLeave}
          >
            <Link
              to="#"
              onClick={() => window.open('https://comece.app/pmo/dashboard', '_blank')}
              className="nav-link nav-link-beat submenu-item"
            >
              <i className="fa-solid fa-diagram-project me-2" style={{ color: '#2980b9' }}></i>
              <span className="sidebar-info">PMO</span>
            </Link>

            {/* GESTÃO DE CUSTOS */}
            <div
              className="tooltip-wrapper"
              onMouseEnter={(e) => handleMouseEnter('gestao-custos', e)}
              onMouseLeave={handleMouseLeave}
            >
              <Link
                to="#"
                onClick={() => window.open('https://comece.app/contratos', '_blank')}
                className="nav-link nav-link-beat submenu-item"
              >
                <i className="fa-solid fa-file-signature me-2" style={{ color: '#16a085' }}></i>
                <span className="sidebar-info">Gestão de Custos</span>
              </Link>
            </div>
          </div>
        </div>
      </div>

      {/* MENU MEMORA.IA — visível para todos; o acesso às páginas é bloqueado
          pelo MemoraGuard para quem não tem memora_ia = 'sim' na auth_user */}
      {(
        <div className="menu-section">
          <div
            className={`nav-link nav-link-beat menu-header ${menuAberto.memoraia ? 'menu-aberto' : ''}`}
            onClick={() => toggleMenu('memoraia')}
          >
            <i className="fa-solid fa-brain me-2 fa-lg" style={{ color: '#d610b5' }}></i>
            <span className="sidebar-info">Memor.IA</span>
            {!memoraIA && (
              <i className="fas fa-lock ms-1" style={{ color: '#adb5bd', fontSize: '0.7rem' }} title="Acesso não liberado"></i>
            )}
            <i className={`fas fa-chevron-down chevron-icon ${menuAberto.memoraia ? 'rotated' : ''}`}></i>
          </div>

          <div className={`submenu ${menuAberto.memoraia ? 'submenu-aberto' : ''}`}>
            <Link
              to="/ListaReunioes"
              className={`nav-link nav-link-beat submenu-item ${location.pathname === '/ListaReunioes' ? 'active' : ''}`}
            >
              <i className="fa-solid fa-video me-2" style={{ color: '#d610b5' }}></i>
              <span className="sidebar-info">Reuniões</span>
            </Link>

            <Link
              to="/configuracao-calendario"
              className={`nav-link nav-link-beat submenu-item ${location.pathname === '/configuracao-calendario' ? 'active' : ''}`}
            >
              <i className="fab fa-google me-2" style={{ color: '#4285f4' }}></i>
              <span className="sidebar-info">Autenticador</span>
            </Link>

            <Link
              to="/Analise-op-memora"
              className={`nav-link nav-link-beat submenu-item ${location.pathname === '/Analise-op-memora' ? 'active' : ''}`}
            >
              <i className="fa-solid fa-chart-line me-2" style={{ color: '#d610b5' }}></i>
              <span className="sidebar-info">Análise Operador</span>
            </Link>

            <Link
              to="/analise-gestor-memora"
              className={`nav-link nav-link-beat submenu-item ${location.pathname === '/analise-gestor-memora' ? 'active' : ''}`}
            >
              <i className="fa-solid fa-users-gear me-2" style={{ color: '#d610b5' }}></i>
              <span className="sidebar-info">Análise Gestor</span>
              {grupoUsuario !== 'adm' && (
                <i className="fas fa-lock ms-1" style={{ color: '#adb5bd', fontSize: '0.65rem' }} title="Restrito a administradores"></i>
              )}
            </Link>

            <Link
              to="/analise-clientes-memora"
              className={`nav-link nav-link-beat submenu-item ${location.pathname === '/analise-clientes-memora' ? 'active' : ''}`}
            >
              <i className="fa-solid fa-building me-2" style={{ color: '#d610b5' }}></i>
              <span className="sidebar-info">Análise de Clientes</span>
              {grupoUsuario !== 'adm' && (
                <i className="fas fa-lock ms-1" style={{ color: '#adb5bd', fontSize: '0.65rem' }} title="Restrito a administradores"></i>
              )}
            </Link>

            <Link
              to="/oportunidades-memora"
              className={`nav-link nav-link-beat submenu-item ${location.pathname === '/oportunidades-memora' ? 'active' : ''}`}
            >
              <i className="fa-solid fa-bullseye me-2" style={{ color: '#d610b5' }}></i>
              <span className="sidebar-info">Vendas e Oportunidades</span>
              {!['adm', 'comercial'].includes(grupoUsuario) && (
                <i className="fas fa-lock ms-1" style={{ color: '#adb5bd', fontSize: '0.65rem' }} title="Restrito aos grupos adm e comercial"></i>
              )}
            </Link>

            <Link
              to="/memora-riscos"
              className={`nav-link nav-link-beat submenu-item ${location.pathname === '/memora-riscos' ? 'active' : ''}`}
            >
              <i className="fas fa-exclamation-triangle me-2" style={{ color: '#dc3545' }}></i>
              <span className="sidebar-info">Riscos</span>
              {grupoUsuario !== 'adm' && (
                <i className="fas fa-lock ms-1" style={{ color: '#adb5bd', fontSize: '0.65rem' }} title="Restrito a administradores"></i>
              )}
            </Link>

            <Link
              to="/memora-intelligence"
              className={`nav-link nav-link-beat submenu-item ${location.pathname === '/memora-intelligence' ? 'active' : ''}`}
            >
              <i className="fa-solid fa-brain me-2" style={{ color: '#00205b' }}></i>
              <span className="sidebar-info">Intelligence</span>
              {grupoUsuario !== 'adm' && (
                <i className="fas fa-lock ms-1" style={{ color: '#adb5bd', fontSize: '0.65rem' }} title="Restrito a administradores"></i>
              )}
            </Link>

            <Link
              to="/memora-documentacao"
              className={`nav-link nav-link-beat submenu-item ${location.pathname === '/memora-documentacao' ? 'active' : ''}`}
            >
              <i className="fa-solid fa-book me-2" style={{ color: '#20c997' }}></i>
              <span className="sidebar-info">Documentação</span>
            </Link>
          </div>
        </div>
      )}

      {/* MENU LIGA COMECE */}
      <div className="menu-section">
        <div 
          className={`nav-link nav-link-beat menu-header ${menuAberto.liga ? 'menu-aberto' : ''}`}
          onClick={() => toggleMenu('liga')}
        >
          <i className="fa-solid fa-trophy me-2 fa-lg" style={{ color: '#f1c40f' }}></i>
          <span className="sidebar-info">Liga Comece</span>
          <i className={`fas fa-chevron-down chevron-icon ${menuAberto.liga ? 'rotated' : ''}`}></i>
        </div>

        <div className={`submenu ${menuAberto.liga ? 'submenu-aberto' : ''}`}>
          <div
            className="tooltip-wrapper"
            onMouseEnter={(e) => handleMouseEnter('runleague', e)}
            onMouseLeave={handleMouseLeave}
          >
            <Link
              to="#"
              onClick={() => window.open('https://comece.app/runleague/home', '_blank')}
              className="nav-link nav-link-beat submenu-item"
            >
              <i className="fa-solid fa-gamepad me-2" style={{ color: '#f1c40f' }}></i>
              <span className="sidebar-info">Run League</span>
            </Link>
          </div>
        </div>
      </div>

      {/* MENU TIME TECNOLOGIA */}
      <div className="menu-section">
        <div 
          className={`nav-link nav-link-beat menu-header ${menuAberto.tecnologia ? 'menu-aberto' : ''}`}
          onClick={() => toggleMenu('tecnologia')}
        >
          <i className="fa-solid fa-microchip me-2 fa-lg" style={{ color: '#1abc9c' }}></i>
          <span className="sidebar-info">Time Tecnologia</span>
          <i className={`fas fa-chevron-down chevron-icon ${menuAberto.tecnologia ? 'rotated' : ''}`}></i>
        </div>

        <div className={`submenu ${menuAberto.tecnologia ? 'submenu-aberto' : ''}`}>
          <div
            className="tooltip-wrapper"
            onMouseEnter={(e) => handleMouseEnter('api-contaazul', e)}
            onMouseLeave={handleMouseLeave}
          >
            <Link
              to="#"
              onClick={() => window.open('https://comece.app/contaazul/tokens', '_blank')}
              className="nav-link nav-link-beat submenu-item"
            >
              <i className="fa-solid fa-key me-2" style={{ color: '#1abc9c' }}></i>
              <span className="sidebar-info">API Conta Azul</span>
            </Link>
          </div>
        </div>
      </div>

      {/* MENU MARKETING */}
      <div className="menu-section">
        <div 
          className={`nav-link nav-link-beat menu-header ${menuAberto.marketing ? 'menu-aberto' : ''}`}
          onClick={() => toggleMenu('marketing')}
        >
          <i className="fa-solid fa-bullhorn me-2 fa-lg" style={{ color: '#e67e22' }}></i>
          <span className="sidebar-info">Marketing</span>
          <i className={`fas fa-chevron-down chevron-icon ${menuAberto.marketing ? 'rotated' : ''}`}></i>
        </div>

        <div className={`submenu ${menuAberto.marketing ? 'submenu-aberto' : ''}`}>
          <div
            className="tooltip-wrapper"
            onMouseEnter={(e) => handleMouseEnter('revops', e)}
            onMouseLeave={handleMouseLeave}
          >
            <Link
              to="#"
              onClick={() => window.open('https://comece.app/revops/home', '_blank')}
              className="nav-link nav-link-beat submenu-item"
            >
              <i className="fa-solid fa-rocket me-2" style={{ color: '#e67e22' }}></i>
              <span className="sidebar-info">Revops</span>
            </Link>
          </div>
        </div>
      </div>

      {/* MENU TESTES — páginas em teste, visível apenas para Eleandro (user_id 2) */}
      {Number(userId) === 2 && (
        <div className="menu-section">
          <div
            className={`nav-link nav-link-beat menu-header ${menuAberto.testes ? 'menu-aberto' : ''}`}
            onClick={() => toggleMenu('testes')}
          >
            <i className="fa-solid fa-flask me-2 fa-lg" style={{ color: '#e84393' }}></i>
            <span className="sidebar-info">Testes</span>
            <i className={`fas fa-chevron-down chevron-icon ${menuAberto.testes ? 'rotated' : ''}`}></i>
          </div>

          <div className={`submenu ${menuAberto.testes ? 'submenu-aberto' : ''}`}>
            <Link
              to="/RelatorioInvestidores/2"
              className={`nav-link nav-link-beat submenu-item ${location.pathname.startsWith('/RelatorioInvestidores/') ? 'active' : ''}`}
            >
              <i className="fa-solid fa-chart-line me-2" style={{ color: '#d4af37' }}></i>
              <span className="sidebar-info">Relatório Investidores</span>
            </Link>

            <Link
              to="/portal-cliente"
              className={`nav-link nav-link-beat submenu-item ${location.pathname.startsWith('/portal-cliente') ? 'active' : ''}`}
            >
              <i className="fa-solid fa-address-card me-2"></i>
              <span className="sidebar-info">Portal do Cliente</span>
            </Link>

            <Link
              to="/historico-auditoria"
              className={`nav-link nav-link-beat submenu-item ${location.pathname === '/historico-auditoria' ? 'active' : ''}`}
            >
              <i className="fa-solid fa-clock-rotate-left me-2" style={{ color: '#6f42c1' }}></i>
              <span className="sidebar-info">Histórico Auditoria</span>
            </Link>

            <Link
              to="/Planejamento_orcamentario/2"
              className={`nav-link nav-link-beat submenu-item ${location.pathname.startsWith('/Planejamento_orcamentario/') ? 'active' : ''}`}
            >
              <i className="fa-solid fa-chart-pie me-2" style={{ color: '#2980b9' }}></i>
              <span className="sidebar-info">Planejamento Orçamentário</span>
            </Link>

            <Link
              to="/Saving_cliente/2"
              className={`nav-link nav-link-beat submenu-item ${location.pathname.startsWith('/Saving_cliente/') ? 'active' : ''}`}
            >
              <i className="fa-solid fa-piggy-bank me-2" style={{ color: '#27ae60' }}></i>
              <span className="sidebar-info">Saving</span>
            </Link>
          </div>
        </div>
      )}

      {/* MENU ADMINISTRAÇÃO — visível apenas para Eleandro (user_id 2) */}
      {Number(userId) === 2 && (
        <div className="menu-section">
          <div
            className={`nav-link nav-link-beat menu-header ${menuAberto.administracao ? 'menu-aberto' : ''}`}
            onClick={() => toggleMenu('administracao')}
          >
            <i className="fa-solid fa-gear me-2 fa-lg" style={{ color: '#34495e' }}></i>
            <span className="sidebar-info">Administração</span>
            <i className={`fas fa-chevron-down chevron-icon ${menuAberto.administracao ? 'rotated' : ''}`}></i>
          </div>

          <div className={`submenu ${menuAberto.administracao ? 'submenu-aberto' : ''}`}>
            <Link
              to="/admin/usuarios"
              className={`nav-link nav-link-beat submenu-item ${location.pathname === '/admin/usuarios' ? 'active' : ''}`}
            >
              <i className="fa-solid fa-user-plus me-2" style={{ color: '#0d6efd' }}></i>
              <span className="sidebar-info">Cadastrar Usuários</span>
            </Link>

            <Link
              to="/admin/atualizacoes"
              className={`nav-link nav-link-beat submenu-item ${location.pathname === '/admin/atualizacoes' ? 'active' : ''}`}
            >
              <i className="fa-solid fa-bullhorn me-2" style={{ color: '#c9a227' }}></i>
              <span className="sidebar-info">Avisos de Atualização</span>
            </Link>

            <Link
              to="/Logados"
              className={`nav-link nav-link-beat submenu-item ${location.pathname === '/Logados' ? 'active' : ''}`}
            >
              <i className="fa-solid fa-clock-rotate-left me-2" style={{ color: '#0d6efd' }}></i>
              <span className="sidebar-info">Acessos / Logados</span>
            </Link>

            <Link
              to="/admin/logs"
              className={`nav-link nav-link-beat submenu-item ${location.pathname === '/admin/logs' ? 'active' : ''}`}
            >
              <i className="fa-solid fa-list-check me-2" style={{ color: '#0d6efd' }}></i>
              <span className="sidebar-info">Trilha de Atividades</span>
            </Link>

            <Link
              to="/admin/webhook-eventos"
              className={`nav-link nav-link-beat submenu-item ${location.pathname === '/admin/webhook-eventos' ? 'active' : ''}`}
            >
              <i className="fa-solid fa-tower-broadcast me-2" style={{ color: '#0d6efd' }}></i>
              <span className="sidebar-info">Webhook Omie</span>
            </Link>

            <Link
              to="/admin/stripe-eventos"
              className={`nav-link nav-link-beat submenu-item ${location.pathname === '/admin/stripe-eventos' ? 'active' : ''}`}
            >
              <i className="fa-brands fa-stripe-s me-2" style={{ color: '#0d6efd' }}></i>
              <span className="sidebar-info">Webhook Stripe</span>
            </Link>

            <Link
              to="/admin/cargas-banco"
              className={`nav-link nav-link-beat submenu-item ${location.pathname === '/admin/cargas-banco' ? 'active' : ''}`}
            >
              <i className="fa-solid fa-database me-2" style={{ color: '#0d6efd' }}></i>
              <span className="sidebar-info">Cargas de Banco</span>
            </Link>

            <Link
              to="/admin/api-bporh"
              className={`nav-link nav-link-beat submenu-item ${location.pathname === '/admin/api-bporh' ? 'active' : ''}`}
            >
              <i className="fa-solid fa-diagram-project me-2" style={{ color: '#0d6efd' }}></i>
              <span className="sidebar-info">APIs do BPO RH</span>
            </Link>

            <Link
              to="/admin/sync-convenia"
              className={`nav-link nav-link-beat submenu-item ${location.pathname === '/admin/sync-convenia' ? 'active' : ''}`}
            >
              <i className="fa-solid fa-rotate me-2" style={{ color: '#0d6efd' }}></i>
              <span className="sidebar-info">Sync Convenia</span>
            </Link>

            <Link
              to="/admin/copiar-banco"
              className={`nav-link nav-link-beat submenu-item ${location.pathname === '/admin/copiar-banco' ? 'active' : ''}`}
            >
              <i className="fa-solid fa-database me-2" style={{ color: '#0d6efd' }}></i>
              <span className="sidebar-info">Copiar Banco FMP</span>
            </Link>

            <Link
              to="/admin/custos-gcp"
              className={`nav-link nav-link-beat submenu-item ${location.pathname === '/admin/custos-gcp' ? 'active' : ''}`}
            >
              <i className="fa-solid fa-cloud me-2" style={{ color: '#c9a227' }}></i>
              <span className="sidebar-info">Custos Google Cloud</span>
            </Link>
            <Link
              to="/Admin/MemoraContestacoes"
              className={`nav-link nav-link-beat submenu-item ${location.pathname === '/Admin/MemoraContestacoes' ? 'active' : ''}`}
            >
              <i className="fa-solid fa-comment-dots me-2" style={{ color: '#0d6efd' }}></i>
              <span className="sidebar-info">Contestações Memor.IA</span>
            </Link>
          </div>
        </div>
      )}

    </nav>
  );

  const renderTooltip = () => {
    if (!tooltipVisivel) return null;

    const tooltipContent = {
      'relatorio-fiscal': 'Emissão de relatórios fiscais do Ecac',
      'dividas': 'Processa relatórios fiscais e faz a análise de clientes que estão com dívida na receita',
      'dctfweb': 'Emite as guias de DCTFWEB',
      'fiscal-base': 'Empresas do fiscal (Acessórias), dia de entrega, responsável e acessos aos portais',
      'fiscal-certificados': 'Certificados A1 das empresas: envio do .pfx, validade e vencimentos',
      'fiscal-config': 'E-mail remetente fiscal@ e cadastro de contador',
      'fiscal-usuarios': 'Quem usa o Fiscal e a conexão da sua conta Google (e-mail e Agenda)',
      'fiscal-como-usar': 'Manual do módulo Fiscal: o que cada menu faz e o passo a passo',
      'fiscal-painel': 'Tudo o que você tem no mês: entregas, tarefas, certificados, Monitor, fechamentos e notas retroativas',
      'fiscal-monitor': 'Download automático das NFS-e do Ambiente Nacional (e São Paulo) por empresa, grupos e notas retroativas',
      'fiscal-fechamento': 'Gera o fechamento fiscal do mês (XLSX com resumo, emitidas e recebidas) a partir das NFS-e nacionais baixadas',
      'fiscal-consulta-chave': 'Baixa o XML de uma NFS-e da Sefin Nacional pela chave de acesso (50 dígitos)',
      'fiscal-faturamento': 'Importa o TXT da Domínio e imprime a declaração de faturamento em uma folha A4',
      'fiscal-entregas': 'Entregas do mês espelhadas no Google Agenda',
      'fiscal-tarefas': 'Demandas internas com histórico e envio ao Acessórias',
      'fiscal-tarefas-acessorias': 'Obrigações fiscais controladas do Acessórias: prazos, atrasos e entregas',
      'pgfn': 'Emite as Guias de PGFN',
      'das': 'Emite as Guias de PGDAS',
      'contabilidade': 'Controle e monitoramento de entregas contábeis',
      'sped-contabil': 'Sistema de escrituração contábil digital',
      'lalur': 'Livro de Apuração do Lucro Real - Controle de adições e exclusões fiscais', // NOVO
      'vision': 'Comece Vision - indicadores e documentos contábeis dos clientes',
      'carteira-contabil': 'Análise da Carteira contábil',
      'gestao-contabil': 'Controle de status atual e histórico de entregas contábeis',
      'devedores-contabil': 'Clientes com documentos em atraso há mais de 60 dias',
      'impostos-recup': 'Controle de clientes com impostos a recuperar',
      'auditoria-balancete': 'Auditoria de balancete e razão contábil assistida por IA',
'historico-auditoria': 'Histórico completo de auditorias contábeis realizadas',
      'historico-envio-lucros-socios': 'Histórico de e-mails de confirmação de distribuição de lucros aos sócios',
      'envio-email-dp': 'Envio de email automático do DP para clientes',
      'guia-fgts': 'Emissão da Guia de FGTS',
      'fmp': 'Controle financeiro e Posição Financeira',
      'gestao-bpo': 'Visão geral de posições e fechamentos de todos os clientes BPO',
      'bpofin-conciliacao-omie': 'Compara a planilha de fluxo do cliente com o extrato do Omie e corrige um lançamento por vez',
      'bpofin-conciliacao-vendizap': 'Lista os títulos em aberto da conta Stripe no Omie, casa cada um com a venda do Stripe e baixa no Omie um por vez ou em lote',
      'bpofin-conciliacao-asaas-vendizap': 'Lista os títulos em aberto da conta ASAAS no Omie, casa cada um com o recebimento Pix do Asaas e baixa no Omie um por vez ou em lote',
      'bpofin-cartao-pixcards': 'Importa a fatura do cartão Pix Cards, sugere a categoria pelo histórico e lança os títulos no contas a pagar do Omie',
      'aspa': 'Sistema de Assinaturas e Procurações Administrativas', // NOVO
      'inventario': 'Controle de equipamentos e patrimônio dos colaboradores',
      'pmo': 'Administração de projetos e gestão de portfólio (PMO)',
      'gestao-custos': 'Gestão de custos e contratos',
      'runleague': 'Ranking e gamificação interna da equipe Comece',
      'api-contaazul': 'Gerenciamento de tokens e integrações com a API Conta Azul',
      'revops': 'Painel de RevOps e estratégias de marketing',
      'lista-reunioes': 'Análise inteligente de reuniões gravadas no Google Meet com IA',
      'configuracao-calendario': 'Conecte o Google Calendar para receber tarefas das reuniões automaticamente',
      'oportunidades': 'Pipeline comercial inteligente com alertas, funil e análise de objeções',
      'riscos': 'Monitoramento de risco de churn com score 0-100 por cliente',
      'intelligence': 'Chat com IA — consultas em linguagem natural sobre reuniões e operadores',
      'portal-cliente': 'Visão 360 do cliente: histórico contábil, fiscal e DP',
    };

    return (
      <div 
        className="custom-tooltip-fixed"
        style={{
          position: 'fixed',
          top: `${tooltipPosition.top}px`,
          left: `${tooltipPosition.left}px`,
          transform: 'translateY(-50%)',
          background: '#F1BE48',
          color: '#00205b',
          padding: '12px 18px',
          borderRadius: '8px',
          fontSize: '13px',
          fontWeight: '600',
          zIndex: 999999,
          boxShadow: '0 4px 15px rgba(0, 0, 0, 0.3)',
          minWidth: '200px',
          maxWidth: '300px',
          whiteSpace: 'normal',
          lineHeight: '1.4',
          pointerEvents: 'none'
        }}
      >
        {tooltipContent[tooltipVisivel]}
        <div style={{
          position: 'absolute',
          right: '100%',
          top: '50%',
          transform: 'translateY(-50%)',
          width: 0,
          height: 0,
          borderTop: '8px solid transparent',
          borderBottom: '8px solid transparent',
          borderRight: '8px solid #F1BE48'
        }}></div>
      </div>
    );
  };

  return (
    <>
      {/* Botão toggle — visível só no mobile */}
      <button
        className="sidebar-mobile-toggle"
        onClick={() => setSidebarAberta(v => !v)}
        aria-label="Abrir menu"
      >
        <i className={`fas ${sidebarAberta ? 'fa-times' : 'fa-chevron-right'}`}></i>
      </button>

      {/* Overlay escurece o fundo quando sidebar está aberta no mobile */}
      {sidebarAberta && (
        <div className="sidebar-overlay" onClick={() => setSidebarAberta(false)} />
      )}

      <div className={`sidebar ${isCliente ? 'sidebar-cliente' : ''} ${sidebarAberta ? 'sidebar-open' : ''}`}>
        <div className="sidebar-header">
          <img src={logo} alt="Logo" className="img-fluid" />
          {isCliente && (
            <div className="cliente-badge mt-2">
              <span className="badge bg-success" style={{ fontSize: '0.7rem' }}>
                <i className="fas fa-briefcase me-1"></i>
                Área do Cliente
              </span>
            </div>
          )}
        </div>
        <hr />
        <p className="welcome-text">
          {isCliente ? 'Olá, ' : 'Bem-vindo(a), '}
          {carregandoNome ? (
            <span className="text-muted">
              <i className="fas fa-spinner fa-spin me-1"></i>
              carregando...
            </span>
          ) : (
            <strong>{username}</strong>
          )}
          {!isCliente && '!'}
        </p>
        
        {inicializado && (isCliente ? renderMenuCliente() : renderMenuInterno())}
        
        {isCliente && (
          <div className="sidebar-footer mt-auto p-3">
            <Link 
              to="/termos-de-uso" 
              className="d-block text-center mb-2"
              style={{
                fontSize: '0.95rem',
                color: '#ffffff',
                textDecoration: 'none',
                opacity: 0.7,
                transition: 'opacity 0.2s'
              }}
              onMouseEnter={(e) => e.target.style.opacity = 1}
              onMouseLeave={(e) => e.target.style.opacity = 0.7}
            >
              Termos de Uso e Política de Privacidade
            </Link>
            
            <Link to="/logout" className="btn btn-outline-danger btn-sm w-100">
              <i className="fas fa-sign-out-alt me-2"></i>
              Sair
            </Link>
          </div>
        )}
      </div>
      
      {renderTooltip()}
    </>
  );
};

export default Sidebar;